import { FootprintingCategory } from "models";

export enum TRANSPORT_COUNTRY_CODE {
  GERMANY = "DE",
  AUSTRIA = "AT",
  SWITZERLAND = "CH",
  POLAND = "PL",
}

// Category colors for footprinting components
export const FOOTPRINTING_CATEGORY_COLORS: Record<FootprintingCategory, string> = {
  fertilizer: "#065C45", // Dark green
  plantProtection: "#DBCCF8", // Purple
  transportation: "#FFB59E", // Salmon
  fieldWork: "#A8DAA5", // Light green
  energy: "#E1F229", // Yellow
  waste: "#A69691", // Brown
  refrigerants: "#B9D0FB", // Lilac
  residue: "#FFA3BF", // Rose
  luc: "#ECE4D2", // Ivory
  other: "#999999", // Grey
};

export const MAIN_COMMODITY_FEED_TYPES = [
  "maize_silage_less_than_25_dm_grown",
  "maize_silage_25_30_dm_grown",
  "maize_silage_30_35_dm_grown",
  "maize_silage_35_40_dm_grown",
  "grass_silage_first_cut_average_grown",
  "wheat_grain_grown",
] as const;

export type ENUM_MAIN_COMMODITY_FEED_TYPES = (typeof MAIN_COMMODITY_FEED_TYPES)[number];

export const MAIN_COMMODITY_CROP_SILAGE_IDS = [
  "f92d8d76-8e33-4afb-b66a-d75120feafb7", // winter_wheat
  "8c40e899-8485-4e18-9ed4-d161fad7488d", // silomais
] as const;

export type ENUM_MAIN_COMMODITY_CROP_SILAGE_IDS = (typeof MAIN_COMMODITY_CROP_SILAGE_IDS)[number];

export function isMainCommodity(identifier?: string, type: "cropId" | "translationSlug" = "translationSlug"): boolean {
  if (!identifier) return false;

  if (type === "cropId") {
    return MAIN_COMMODITY_CROP_SILAGE_IDS.includes(identifier as ENUM_MAIN_COMMODITY_CROP_SILAGE_IDS);
  }

  return MAIN_COMMODITY_FEED_TYPES.includes(identifier as ENUM_MAIN_COMMODITY_FEED_TYPES);
}

export interface ComparisonCropMapping {
  id: string;
  regionalCropComparisonValueCO2ekg: number | null;
}

export const REGIONAL_COMPARISON_CROP_MAPPINGS: ComparisonCropMapping[] = [
  {
    id: "e03edf65-78bd-40de-b071-dff72dde253e", // Winter Rapeseed
    regionalCropComparisonValueCO2ekg: 0.9,
  },
  {
    id: "b136349e-a0cd-43ff-842d-47fb0a1c213b", // Summer Rapeseed
    regionalCropComparisonValueCO2ekg: 0.9,
  },
  {
    id: "25cfaa8f-2246-468f-bceb-1739d78d28a8", // Sunflowers
    regionalCropComparisonValueCO2ekg: 1.41,
  },
  {
    id: "493e3513-88e7-49e4-8509-3843d77882b2", // Grain maize
    regionalCropComparisonValueCO2ekg: 0.47,
  },
  {
    id: "f92d8d76-8e33-4afb-b66a-d75120feafb7", // Winter wheat
    regionalCropComparisonValueCO2ekg: 0.52,
  },
  {
    id: "7153e8d4-73d7-45cd-bc12-62737e3929fe", // Soybeans
    regionalCropComparisonValueCO2ekg: 0.86,
  },
  {
    id: "5433e212-f24a-42e2-ab3b-3030bfa3816e", // Winter Barley
    regionalCropComparisonValueCO2ekg: 0.49,
  },
  {
    id: "4f8bfe44-2103-4e70-9088-53c5ddce2008", // Summer Barley
    regionalCropComparisonValueCO2ekg: 0.49,
  },
  {
    id: "66b9fae6-2453-47ab-bc9b-5256161a5439", // Winter Rye
    regionalCropComparisonValueCO2ekg: 0.59,
  },
  {
    id: "65d06947-3e8c-4839-a707-b1b646f8be5a", // Summer Rye
    regionalCropComparisonValueCO2ekg: 0.59,
  },
  {
    id: "b0226855-f9e7-47d0-bdd2-1b2384f5b77c", // Winter Triticale
    regionalCropComparisonValueCO2ekg: 0.52,
  },
  {
    id: "d43064cc-c44d-44c7-8b2a-95cbd73ed769", // Summer Triticale
    regionalCropComparisonValueCO2ekg: 0.52,
  },
  {
    id: "ba08dfee-18ac-4113-88c3-e878055e2306", // Winter Oats
    regionalCropComparisonValueCO2ekg: 0.58,
  },
  {
    id: "15b8a95c-6231-47e6-9022-4eafeb4e0786", // Summer Oats
    regionalCropComparisonValueCO2ekg: 0.58,
  },
  {
    id: "ee5d825e-5e7d-478e-bc93-9b762c7360ae", // Potatoes
    regionalCropComparisonValueCO2ekg: 0.15,
  },
  {
    id: "99beb3e1-2622-4ab2-9827-403c261a8b66", // Sugar beets
    regionalCropComparisonValueCO2ekg: 0.67,
  },
  {
    id: "693e37be-5455-4a73-a290-2fdbf4d43ebe", // Spring wheat
    regionalCropComparisonValueCO2ekg: 0.59,
  },
  {
    id: "4887b046-2a5f-4d87-b9d7-10cce7f6b206", // Alfalfa
    regionalCropComparisonValueCO2ekg: 0.87,
  },
  {
    id: "bf02e1a9-8fc9-4ed0-b069-17460edb867d", // Fava beans
    regionalCropComparisonValueCO2ekg: 0.64,
  },
  {
    id: "87dd97ad-de8a-4aaf-ab82-c74826f61011", // Peas
    regionalCropComparisonValueCO2ekg: 0.59,
  },
  {
    id: "01dcc28f-a97c-4237-861a-92beaae33d8c", // Perennial grassland
    regionalCropComparisonValueCO2ekg: null,
  },
  {
    id: "c235ffeb-2ced-4147-b68e-9c88efb245ec", // Clover grass
    regionalCropComparisonValueCO2ekg: null,
  },
];

export const getCropById = (id: string) => REGIONAL_COMPARISON_CROP_MAPPINGS.find((crop) => crop.id === id);
